// AUPRÉA — Moteur de préconisations (scores + génération + 3 formules)
// Pure fonctions, aucune dépendance React. Utilisé par Preconisations.jsx.

// ── Utils ───────────────────────────────────────────────────────────────────
const roundPct = (v) => Math.round(v * 100) / 100;
const toArray = (o) => (Array.isArray(o) ? o : []);

// Calcul TTC pour prestations HT selon TVA des réglages
const priceTTC = (prestation, tvaTauxPct) => {
  if (prestation.prix == null) return 0;
  if (prestation.base_prix === "HT") {
    return roundPct(prestation.prix * (1 + (tvaTauxPct || 0) / 100));
  }
  return prestation.prix;
};

const findPrestation = (prestations, code) => prestations.find(p => p.code === code);

const libelleGlobal = (pct) => {
  if (pct >= 80) return { label: "Bien préparé",         color: "#0F6E56" };
  if (pct >= 50) return { label: "Partiellement préparé",color: "#B45309" };
  return                { label: "Préparation insuffisante", color: "#991B1B" };
};
const libellePilier = (pct) => {
  if (pct >= 80) return { label: "Solide",       color: "#0F6E56" };
  if (pct >= 50) return { label: "En bonne voie",color: "#B45309" };
  return                { label: "À renforcer",  color: "#991B1B" };
};

// ── SCORES ──────────────────────────────────────────────────────────────────
function scoreSuccession(rep) {
  const s = (id) => ({ oui: 2, partiellement: 1, non: 0 }[rep[id]] ?? 0);
  const p1 = s("s1q1")+s("s1q2")+s("s1q3")+s("s1q4")+s("s1q5"); // /10
  const p2 = s("s2q1")+s("s2q2")+s("s2q3")+s("s2q4")+s("s2q5"); // /10
  const p3 = s("s3q1")+s("s3q2")+s("s3q3")+s("s3q4")+s("s3q5"); // /10
  const total = p1 + p2 + p3; // /30
  const pct = Math.round(total * 100 / 30);
  return { p1, p2, p3, total, pct, libGlobal: libelleGlobal(pct),
    pilierLibs: { p1: libellePilier(p1*10), p2: libellePilier(p2*10), p3: libellePilier(p3*10) } };
}

function scoreDirigeants(rep) {
  const s = (id) => {
    const v = rep[id];
    if (v === "a") return 2; if (v === "b") return 1; if (v === "c") return 0;
    if (v === "nc") return null; return null;
  };
  // S1 = d1q1+d1q2+d1q3  /6
  // S2 = d2q1+d2q2+d2q3(if not nc)  /6
  // S3 = d3q1+d3q2+d3q3  /6
  // S4 = d4q1+d4q2       /4
  // S5 = d5q1+d5q2+d5q3  /6
  const sect = (ids, maxPerQ = 2) => {
    let sum = 0, max = 0;
    ids.forEach(id => {
      const v = s(id);
      if (v !== null) { sum += v; max += maxPerQ; }
    });
    return { sum, max, pct: max ? Math.round(sum * 100 / max) : 0 };
  };
  const S1 = sect(["d1q1","d1q2","d1q3"]);
  const S2 = sect(["d2q1","d2q2","d2q3"]);
  const S3 = sect(["d3q1","d3q2","d3q3"]);
  const S4 = sect(["d4q1","d4q2"]);
  const S5 = sect(["d5q1","d5q2","d5q3"]);
  const globSum = S1.sum + S2.sum + S3.sum + S4.sum + S5.sum;
  const globMax = S1.max + S2.max + S3.max + S4.max + S5.max;
  const pct = globMax ? Math.round(globSum * 100 / globMax) : 0;
  return {
    S1, S2, S3, S4, S5, globSum, globMax, pct,
    libGlobal: libelleGlobal(pct),
    sectionLibs: { S1: libellePilier(S1.pct), S2: libellePilier(S2.pct), S3: libellePilier(S3.pct), S4: libellePilier(S4.pct), S5: libellePilier(S5.pct) },
  };
}

function calculerComplexiteOuvrir(parcours) {
  const spec = parcours.donnees_specifiques || {};
  const d = spec.defunt || {};
  const h = spec.heritiers || {};
  const p = spec.patrimoine || {};
  const c = spec.climat || {};
  const docs = spec.docs || {};
  let pts = 0;
  const raisons = [];
  if (h.h_introuvables === "Oui")      { pts += 2; raisons.push("Héritiers introuvables (+2)"); }
  if (h.h_diaspora === "Oui")          { pts += 1; raisons.push("Diaspora (+1)"); }
  if (c.c_desaccords === "Oui")        { pts += 2; raisons.push("Désaccords entre héritiers (+2)"); }
  if (parseInt(h.h_nombre || "0", 10) > 5) { pts += 1; raisons.push("Plus de 5 héritiers (+1)"); }
  // Décès > 10 ans
  const dateDeces = d.def_date;
  let anciennete10 = false;
  if (dateDeces) {
    const y = new Date(dateDeces).getFullYear();
    if (!isNaN(y) && (new Date().getFullYear() - y) >= 10) { pts += 2; raisons.push("Décès il y a plus de 10 ans (+2)"); anciennete10 = true; }
  }
  const nbAObtenir = Object.values(docs).filter(v => v === "À obtenir").length;
  if (nbAObtenir >= 3) { pts += 1; raisons.push("≥ 3 documents à obtenir (+1)"); }
  if (d.def_notaire === "Non") { pts += 1; raisons.push("Pas de notaire désigné (+1)"); }
  const niveau = pts <= 1 ? "Succession simple" : (pts <= 3 ? "Succession intermédiaire" : "Succession complexe");
  return { points: pts, niveau, raisons, anciennete10, biensImmo: p.p_immo === "Oui",
    dirigeantDefunt: d.def_dirigeant === "Oui", nbHeritiers: parseInt(h.h_nombre || "0", 10),
    diaspora: h.h_diaspora === "Oui", introuvables: h.h_introuvables === "Oui",
    desaccords: c.c_desaccords === "Oui", nbAObtenir };
}

function calculerVerdictFoncier(parcours) {
  const spec = parcours.donnees_specifiques || {};
  const rep = parcours.reponses || {};
  const anneeDebut = parseInt(spec.possession?.poss_annee || rep.poss_annee || "0", 10);
  const annees = anneeDebut > 0 ? (new Date().getFullYear() - anneeDebut) : 0;
  const bloc =
    (rep.f1 && rep.f1 !== "Oui") || (rep.f2 && rep.f2 !== "Oui") ||
    (rep.f3 && rep.f3 !== "Oui") || (rep.f4 && rep.f4 !== "Oui") ||
    (rep.f5 && rep.f5 !== "Non");
  let verdict = "";
  let color = "#B45309";
  if (bloc) { verdict = "Conditions actuellement non réunies"; color = "#991B1B"; }
  else if (rep.f6 === "Oui" && rep.f7 === "Oui" && annees >= 10) { verdict = "Conditions a priori réunies · prescription abrégée"; color = "#0F6E56"; }
  else if (annees >= 30) { verdict = "Conditions a priori réunies · prescription trentenaire"; color = "#0F6E56"; }
  else { verdict = `Délai non atteint (${annees} an${annees > 1 ? "s" : ""} d'occupation)`; color = "#B45309"; }
  return { verdict, color, annees, conditionsReunies: verdict.startsWith("Conditions a priori réunies") };
}

// ── PRÉCONISATIONS ──────────────────────────────────────────────────────────
const P = (code, texte, opts = {}) => ({ code, texte, coche: opts.coche !== false, isOption: !!opts.isOption });
const ALERTE = (texte) => ({ code: "__ALERTE__", texte, coche: false, isAlerte: true });

function precoSuccession(parcours) {
  const rep = parcours.reponses || {};
  const comp = parcours.reponses_complementaires || {};
  const sc = scoreSuccession(rep);
  const prec = [];
  const alertes = [];

  // Carnet
  let carnet = "ESS";
  if (sc.p1 <= 5) { prec.push(P("AUDIT", `Organisation administrative fragile (${sc.p1}/10) : recensement complet des documents et des actifs.`)); carnet = "CONF"; }
  else if (sc.p1 <= 8) prec.push(P("DUO", `Organisation partiellement en place (${sc.p1}/10) : le carnet et 2h d'accompagnement suffisent.`));

  // Atelier
  const causes = [];
  if (rep.s1q5 === "non") causes.push("proches qui ne savent pas où trouver les documents");
  if (rep.s2q2 === "non") causes.push("aucune personne de confiance");
  if (rep.s2q5 === "non") causes.push("volontés funéraires non exprimées");
  if (comp.recompose === "oui") causes.push("famille recomposée ou dispersée");
  if (causes.length) prec.push(P("ATELIER", `Réunion familiale utile : ${causes.join(" · ")}.`));

  // Pilier 2
  if (sc.p2 <= 5) {
    carnet = "PPLUS";
    prec.push(P("ORI_NOT", `Protection juridique insuffisante (${sc.p2}/10) : testament et mandat de protection future à voir avec un notaire.`));
  }
  // Pilier 3
  if (sc.p3 <= 5) prec.push(P("ORI_CGP", `Volet financier et fiscal à travailler (${sc.p3}/10) : AUPRÉA ne donne pas de conseil fiscal, orientation vers un expert.`));

  // Indivision → Foncier Clair
  if (comp.indivision === "oui") prec.push(P("FCF", "Bien en indivision ou sans titre signalé : diagnostic Foncier Clair."));

  // Suivi
  const suiviCoche = sc.pct >= 80;
  prec.push(P("SUIVI", "Suivi annuel pour actualiser l'inventaire et l'organisation.", { coche: suiviCoche, isOption: !suiviCoche }));

  // Carnet retenu
  prec.push(P(carnet, `Carnet digital ${carnet === "ESS" ? "Essentiel" : (carnet === "CONF" ? "Confort" : "Patrimoine+")}.`));

  // Option consultation
  prec.push(P("CONS", "Consultation patrimoniale initiale (approfondissement).", { coche: false, isOption: true }));

  // Alertes consultant
  if (comp.deces === "oui") alertes.push(ALERTE("Décès récent : lancez aussi le parcours Ouvrir une succession."));
  if (comp.dirigeant === "oui") alertes.push(ALERTE("Le client est chef d'entreprise : proposez le Bilan Dirigeants lors du même rendez-vous."));

  return { scores: sc, preconisations: prec, alertes };
}

function precoDirigeants(parcours) {
  const rep = parcours.reponses || {};
  const comp = parcours.reponses_complementaires || {};
  const spec = parcours.donnees_specifiques || {};
  const sc = scoreDirigeants(rep);
  const prec = [];
  const alertes = [];

  const dsize = comp.dsize;
  const dassoc = comp.dassoc;
  const dcession = comp.dcession;
  const big = dsize === "10 salariés et plus" || dassoc === "Plusieurs associés";

  // Âge
  const fiche = parcours.fiche || {};
  let age = 0;
  if (fiche.date_naissance) {
    const d = new Date(fiche.date_naissance);
    if (!isNaN(d)) age = new Date().getFullYear() - d.getFullYear();
  }

  const faibleGouv = sc.S1.sum <= 3 || rep.d5q2 === "c";
  const faibleFamille = sc.S4.sum <= 2 || rep.d3q1 === "b";
  const faibleStructure = sc.S2.sum <= 3 || sc.S3.sum <= 3;

  let packAjoute = null;
  if (!big) {
    if (dcession === "Dans moins de 2 ans" || dcession === "Dans 2 à 5 ans" || age >= 55) {
      prec.push(P("PACKT", `Transmission à préparer (cession envisagée${age >= 55 ? " ou âge ≥ 55" : ""}) : pack complet.`));
      packAjoute = "PACKT";
    } else if (faibleFamille) {
      prec.push(P("PACKS", "Protection familiale insuffisante : pack sérénité (Plan de Continuité + atelier + suivi + Patrimoine+)."));
      packAjoute = "PACKS";
    } else if (faibleGouv) {
      prec.push(P("PACKC", "Gouvernance fragile : pack continuité (Plan de Continuité + 1 an de suivi)."));
      packAjoute = "PACKC";
    } else {
      prec.push(P("PCD", "Bonne base : formaliser l'existant dans un dossier de continuité."));
      packAjoute = "PCD";
    }
  } else {
    prec.push(P("PCDM", "TPE/PME avec associés : Plan de Continuité Dirigeant multi-associés."));
    packAjoute = "PCDM";
    if (faibleFamille) prec.push(P("AGF", "Situation familiale exposée : Atelier de Gouvernance Familiale."));
    alertes.push(ALERTE("Packs sur devis pour les entreprises de 10 salariés et plus ou avec associés."));
  }

  if (faibleStructure && packAjoute !== "PACKT") prec.push(P("COORD", "Structure ou valorisation à consolider : coordination avec experts (valorisation, statuts et pacte, transmission, homme-clé)."));
  if (sc.S4.sum <= 2) prec.push(P("ORI_ASS", "Protection famille faible : orientation assureur (homme-clé, prévoyance dirigeant)."));

  // Option suivi si aucun pack global
  if (!["PACKC","PACKS","PACKT"].includes(packAjoute)) prec.push(P("SUIVD", "Suivi annuel dirigeant en option.", { coche: false, isOption: true }));
  // Patrimoine+ personnel si pas PACKS
  if (packAjoute !== "PACKS") prec.push(P("PPLUS", "Carnet digital Patrimoine+ (patrimoine personnel du dirigeant).", { coche: false, isOption: true }));

  // Option Consultation dirigeant
  prec.push(P("CONSD", "Consultation Dirigeant (approfondissement).", { coche: false, isOption: true }));

  alertes.push(ALERTE("Un dirigeant est aussi un particulier : enchaînez sur le Bilan Succession."));

  return { scores: sc, preconisations: prec, alertes };
}

function precoOuvrir(parcours) {
  const cx = calculerComplexiteOuvrir(parcours);
  const prec = [];
  const alertes = [];
  const pros = ["Notaire"];
  if (cx.introuvables) pros.push("Généalogiste successoral");
  if (cx.desaccords)   pros.push("Médiateur familial");
  if (cx.anciennete10 && cx.biensImmo) pros.push("Commissaire de justice");

  prec.push(P("FDR", "Feuille de route succession : ordre des démarches personnalisées."));
  if (cx.nbAObtenir >= 3 || cx.diaspora || cx.points >= 2)
    prec.push(P("POSTDC", "Complexité administrative (documents manquants / diaspora / complexité) : forfait administratif post-décès."));
  if (cx.desaccords) prec.push(P("ATELIER", "Désaccords entre héritiers : atelier de sérénité familiale (médiation)."));
  if (cx.biensImmo && cx.anciennete10) prec.push(P("FCF", "Bien immobilier + décès ancien : fiche de synthèse Foncier Clair."));
  if (cx.dirigeantDefunt) prec.push(P("POSTDD", "Le défunt était chef d'entreprise : forfait post-décès dirigeant."));

  prec.push(P("CONS", "Consultation patrimoniale initiale (approfondissement).", { coche: false, isOption: true }));

  if (cx.anciennete10) alertes.push(ALERTE("Complexité relevée (délai d'option des héritiers, indivision probable), à faire confirmer par le notaire."));
  if (cx.nbHeritiers > 1) alertes.push(ALERTE(`${cx.nbHeritiers} bilans particuliers possibles : proposez-leur le Bilan Succession gratuit.`));

  return {
    scores: { complexite: cx.points, niveau: cx.niveau, raisons: cx.raisons, libGlobal: { label: cx.niveau, color: cx.points <= 1 ? "#0F6E56" : (cx.points <= 3 ? "#B45309" : "#991B1B") } },
    pros, preconisations: prec, alertes,
  };
}

function precoFoncier(parcours) {
  const v = calculerVerdictFoncier(parcours);
  const prec = [];
  const alertes = [];
  prec.push(P("FCF", "Fiche de synthèse Foncier Clair : régime, base légale, années calculées, documents à réunir."));
  prec.push(P("FCA", "Accompagnement Foncier Clair : dossier de preuves + coordination commissaire de justice / notaire.",
    { coche: v.conditionsReunies, isOption: !v.conditionsReunies }));
  prec.push(P("CONS", "Consultation patrimoniale initiale (approfondissement).", { coche: false, isOption: true }));
  alertes.push(ALERTE("Verdict indicatif, fondé sur les réponses du client, à confirmer par un notaire."));
  return {
    scores: { verdict: v.verdict, color: v.color, annees: v.annees, libGlobal: { label: v.verdict, color: v.color } },
    preconisations: prec, alertes,
  };
}

// Remises automatiques
function appliquerRemises(preconisations) {
  const codes = new Set(preconisations.filter(p => p.coche && p.code !== "__ALERTE__").map(p => p.code));
  if (codes.has("FDR") && codes.has("POSTDC") && !codes.has("REM89"))
    preconisations.push(P("REM89", "Remise automatique : FDR déduite du forfait post-décès.", { coche: true }));
  if (codes.has("FCF") && codes.has("FCA") && !codes.has("REM69"))
    preconisations.push(P("REM69", "Remise automatique : FCF déduite de l'accompagnement.", { coche: true }));
  return preconisations;
}

// Point d'entrée
export function computeAnalyse(parcours) {
  if (!parcours) return null;
  const map = {
    bilan_succession: precoSuccession,
    bilan_dirigeants: precoDirigeants,
    ouvrir_succession: precoOuvrir,
    foncier_clair: precoFoncier,
  };
  const fn = map[parcours.type_parcours];
  if (!fn) return null;
  const analyse = fn(parcours);
  analyse.preconisations = appliquerRemises([...analyse.preconisations]);
  return analyse;
}

// ── FORMULES ────────────────────────────────────────────────────────────────
// Retourne : { lignes: [{code, prestation, prixTTC, isRemise, isOrientation, isAbo}], totalOneShot, totalMonthly, carnet }
function buildFormule(preconisations, prestations, reglages, opts = {}) {
  const tva = reglages?.tva_taux ?? 0;
  const lignes = [];
  let totalOneShot = 0;
  let totalMonthly = 0;
  let carnet = null;

  preconisations.forEach(p => {
    if (p.code === "__ALERTE__") return;
    if (!p.coche) return;
    const pr = findPrestation(prestations, p.code);
    if (!pr) return;
    const prixTTC = priceTTC(pr, tva);
    const isAbo = pr.type === "abonnement_mensuel";
    const isOrientation = pr.type === "orientation_gratuite";
    const isRemise = pr.type === "remise";
    lignes.push({ code: pr.code, nom: pr.nom, description: pr.description, prixTTC, base_prix: pr.base_prix, type: pr.type, isAbo, isOrientation, isRemise, unite: pr.unite });
    if (isAbo) { totalMonthly += prixTTC; if (!carnet) carnet = pr.code; }
    else if (!isOrientation) totalOneShot += prixTTC;
  });
  return { lignes, totalOneShot: roundPct(totalOneShot), totalMonthly: roundPct(totalMonthly), carnet };
}

export function buildFormules(preconisations, prestations, reglages) {
  const tva = reglages?.tva_taux ?? 0;

  // ── Recommandée = ce qui est coché
  const recPre = preconisations.filter(p => p.code !== "__ALERTE__");
  const recommande = buildFormule(recPre, prestations, reglages);

  // ── Essentiel = 1re prestation principale de Recommandée + orientations + carnet ESS
  const principale = recommande.lignes.find(l => !l.isOrientation && !l.isRemise && !l.isAbo);
  const essentielCoche = [];
  if (principale) essentielCoche.push({ code: principale.code, coche: true });
  recommande.lignes.filter(l => l.isOrientation).forEach(l => essentielCoche.push({ code: l.code, coche: true }));
  essentielCoche.push({ code: "ESS", coche: true });
  let essentiel = buildFormule(essentielCoche, prestations, reglages);
  // Si résultat identique à la Recommandée → retirer le carnet
  const identique = essentiel.lignes.length === recommande.lignes.length && essentiel.totalOneShot === recommande.totalOneShot && essentiel.totalMonthly === recommande.totalMonthly;
  if (identique) essentiel = buildFormule(essentielCoche.filter(l => l.code !== "ESS"), prestations, reglages);

  // ── Complet = Recommandé + toutes options + carnet PPLUS
  const completCoche = preconisations
    .filter(p => p.code !== "__ALERTE__")
    .map(p => ({ code: p.code, coche: true })); // tout activé
  // Remplacer carnet par PPLUS : retirer ESS/CONF s'ils sont là
  const noCarnet = completCoche.filter(l => !["ESS","CONF","PPLUS"].includes(l.code));
  noCarnet.push({ code: "PPLUS", coche: true });
  const complet = buildFormule(noCarnet, prestations, reglages);
  // Réappliquer les remises pour la version complet
  const codes = new Set(noCarnet.map(l => l.code));
  const remiseLignes = [];
  if (codes.has("FDR") && codes.has("POSTDC")) remiseLignes.push({ code: "REM89", coche: true });
  if (codes.has("FCF") && codes.has("FCA")) remiseLignes.push({ code: "REM69", coche: true });
  if (remiseLignes.length) {
    remiseLignes.forEach(rl => {
      const pr = findPrestation(prestations, rl.code);
      if (pr && !complet.lignes.find(l => l.code === rl.code)) {
        const prixTTC = priceTTC(pr, tva);
        complet.lignes.push({ code: pr.code, nom: pr.nom, description: pr.description, prixTTC, base_prix: pr.base_prix, type: pr.type, isRemise: true });
        complet.totalOneShot = roundPct(complet.totalOneShot + prixTTC);
      }
    });
  }

  return { recommande, essentiel, complet };
}
